import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
    mapCollection,
    setAddCollectionModal,
    setSelectedCollection,
} from "./AddCollectionModalSlice";
import { clearSelectedProducts } from "../../pages/productRangePage/ProductRangeSlice";
import { JSX, useEffect, useState } from "react";
import Select, { type SingleValue } from "react-select";
import {
    useCreateCollectionMutation,
    useGetCollectionsQuery,
    useLazyGetCollectionsQuery,
    useUpdateCollectionMutation,
} from "../../pages/collectionsPage/CollectionsSlice";
import { RouteEnum } from "../../common/enums/RouteEnum";
import { useNavigate } from "react-router-dom";
import { Modal, Button } from "@hatch-digital-nl/bravilor-storybook";
import { useStringTranslation } from "../../common/hooks/useStringTranslation";

export const AddCollectionModalContainer = (): JSX.Element => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const { data } = useGetCollectionsQuery();
    const { t } = useStringTranslation();

    const [collectionTrigger] = useLazyGetCollectionsQuery();
    const [createCollectionTrigger] = useCreateCollectionMutation();
    const [updateCollectionTrigger] = useUpdateCollectionMutation();

    const { collections } = useAppSelector((state) => state.collections);
    const { selectedProductIds, collectionId, collectionOptions } =
        useAppSelector((state) => state.addCollectionModal);

    const [collectionType, setCollectionType] = useState<string>("new");

    const [loading, setLoading] = useState<boolean>(false);
    const [collectionName, setCollectionName] = useState<string>("");

    const handleClose = (): void => {
        dispatch(
            setAddCollectionModal({
                showPopup: false,
            }),
        );
    };

    const addCollectionSubmitHandler = (): void => {
        setLoading(true);
        if (collectionType === "new") {
            const paramObject = {
                collectionName: collectionName,
                productIds: selectedProductIds,
            };

            createCollectionTrigger(paramObject).then(() => {
                collectionTrigger().then(() => {
                    setLoading(false);
                    dispatch(
                        setAddCollectionModal({
                            showPopup: false,
                        }),
                    );
                });
            });
        } else {
            const paramObject = {
                collectionId: collectionId,
                collectionName:
                    collections.find(
                        (item: { id: number }) => item.id === collectionId,
                    )?.name || "",
                productIds: [
                    ...(collections.find(
                        (item: { id: number }) => item.id === collectionId,
                    )?.productIds || []),
                    ...selectedProductIds,
                ],
            };

            updateCollectionTrigger(paramObject).then(() => {
                collectionTrigger().then(() => {
                    setLoading(false);
                    dispatch(
                        setAddCollectionModal({
                            showPopup: false,
                        }),
                    );
                });
            });
        }
        dispatch(clearSelectedProducts());
        navigate(RouteEnum.COLLECTIONS);
    };

    useEffect(() => {
        if (data) {
            dispatch(mapCollection(data));
        }
    }, [dispatch, data]);

    return (
        <Modal onClose={handleClose} loading={loading}>
            <header className="c-modal__header">
                <h2>{t("how-do-you-want-to-save-this-selection")}</h2>
            </header>
            <div className="c-modal__body">
                <p>{t("add-the-selection-to-an")}</p>

                <div className="c-modal__body__radio-input">
                    <div
                        className="c-modal__body__radio-input__group"
                        onClick={() => {
                            setCollectionType("new");
                        }}
                    >
                        <input
                            type="radio"
                            id="new"
                            name="collection-type"
                            readOnly={true}
                            checked={collectionType === "new"}
                        />
                        <label>{t("new-collection")}</label>
                    </div>
                    <div
                        className="c-modal__body__radio-input__group"
                        onClick={() => {
                            setCollectionType("existing");
                        }}
                    >
                        <input
                            type="radio"
                            id="existing"
                            name="collection-type"
                            readOnly={true}
                            checked={collectionType === "existing"}
                        />
                        <label>{t("existing-collection")}</label>
                    </div>
                </div>

                {collectionType === "new" ? (
                    <div className="c-modal__body__input">
                        <label>{t("collection-name")}</label>
                        <input
                            type="text"
                            value={collectionName}
                            onChange={(e) => setCollectionName(e.target.value)}
                        />
                    </div>
                ) : (
                    <div className="c-modal__body__input">
                        {!!collectionOptions && (
                            <>
                                <label>Collection name</label>
                                <Select
                                    className="collection-select"
                                    classNamePrefix="select"
                                    name="collections"
                                    isLoading={false}
                                    isClearable={false}
                                    isSearchable={true}
                                    options={collectionOptions}
                                    onChange={(
                                        selectedOption: SingleValue<{
                                            value: string;
                                        }>,
                                    ) => {
                                        if (!selectedOption) {
                                            return;
                                        }

                                        dispatch(
                                            setSelectedCollection(
                                                Number(selectedOption.value),
                                            ),
                                        );
                                    }}
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            height: "4.5rem",
                                            minHeight: "4.5rem",
                                        }),
                                        input: (provided) => ({
                                            ...provided,
                                            margin: "0px",
                                            height: "4.5rem",
                                            minHeight: "4.5rem",
                                        }),
                                        valueContainer: (provided) => ({
                                            ...provided,
                                            height: "4.5rem",
                                            minHeight: "4.5rem",
                                            padding: "0 1rem",
                                        }),
                                        indicatorsContainer: (provided) => ({
                                            ...provided,
                                            height: "4.5rem",
                                            minHeight: "4.5rem",
                                        }),
                                    }}
                                />
                            </>
                        )}
                    </div>
                )}

                <div className="c-modal__body__buttons">
                    <Button
                        variant="primary"
                        onClick={addCollectionSubmitHandler}
                    >
                        <span>{t("save-collection")}</span>
                    </Button>

                    <Button variant="secondary" onClick={handleClose}>
                        <span>{t("cancel")}</span>
                    </Button>
                </div>
            </div>
        </Modal>
    );
};
